import { makeAutoObservable, runInAction } from "mobx";
import OrderAPI from "../api/OrderAPI";
import ImageAPI from "../api/ImageAPI";
import { HelperAPI } from "../api/HelperAPI";
import { APIBlobResponse } from "../../../models/NetworkResponse";
import {
  CLIENT_PO_UPLOAD_CONTAINER,
  MAX_CLIENT_POS_PER_ORDER,
  OrderClientPO,
  UpdateClientPORequest,
} from "../../../models/Order/OrderClientPO";

const extractErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message ||
  (error?.message === "Network Error"
    ? "Unable to reach the server. Please check your connection and try again."
    : error?.message) ||
  fallback;

/** Client POs of one order: list, add, edit and remove (v1 API). */
export class OrderClientPOStore {
  private orderAPI = new OrderAPI();
  private imageAPI = new ImageAPI();
  /** Uploaded URL per picked file, so a failed save can be retried without uploading again. */
  private uploadedUrls = new WeakMap<File, string>();

  orderId: number | null = null;
  clientPOs: OrderClientPO[] = [];

  isLoading: boolean = false;
  loadError: string = "";

  isAdding: boolean = false;
  addError: string = "";

  editingId: number | null = null;
  isSavingEdit: boolean = false;
  editError: string = "";

  selectedIds: number[] = [];
  isRemoving: boolean = false;
  removeError: string = "";

  downloadingId: number | null = null;
  downloadError: string = "";

  successMessage: string = "";

  constructor() {
    makeAutoObservable<
      OrderClientPOStore,
      "orderAPI" | "imageAPI" | "uploadedUrls"
    >(this, { orderAPI: false, imageAPI: false, uploadedUrls: false });
  }

  get canAddMore(): boolean {
    return this.clientPOs.length < MAX_CLIENT_POS_PER_ORDER;
  }

  get isBusy(): boolean {
    return this.isAdding || this.isSavingEdit || this.isRemoving;
  }

  get allSelected(): boolean {
    return (
      this.clientPOs.length > 0 &&
      this.selectedIds.length === this.clientPOs.length
    );
  }

  setOrderId = (orderId: number | null) => {
    if (this.orderId === orderId) return;
    this.orderId = orderId;
    this.clientPOs = [];
    this.selectedIds = [];
    this.editingId = null;
    this.clearMessages();
  };

  clearMessages = () => {
    this.loadError = "";
    this.addError = "";
    this.editError = "";
    this.removeError = "";
    this.downloadError = "";
    this.successMessage = "";
  };

  /** Clears messages, selection and edit mode. Called when the popup opens. */
  resetView = () => {
    this.clearMessages();
    this.selectedIds = [];
    this.editingId = null;
  };

  private setList = (list: OrderClientPO[]) => {
    this.clientPOs = list;
    const ids = new Set(list.map((po) => po.id));
    this.selectedIds = this.selectedIds.filter((id) => ids.has(id));
    if (this.editingId !== null && !ids.has(this.editingId)) {
      this.editingId = null;
    }
  };

  private requireOrderId = (): number => {
    if (!this.orderId) {
      throw new Error("A saved order is required to manage client POs.");
    }
    return this.orderId;
  };

  loadClientPOs = async (): Promise<void> => {
    if (!this.orderId) return;
    const orderId = this.orderId;
    this.isLoading = true;
    this.loadError = "";
    try {
      const list = await this.orderAPI.getClientPOs(orderId);
      runInAction(() => {
        if (this.orderId === orderId) this.setList(list);
      });
    } catch (error: any) {
      runInAction(() => {
        this.loadError = extractErrorMessage(
          error,
          "Failed to load client POs.",
        );
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  /** Re-fetches the list in place, keeping selection and edit mode where the POs still exist. */
  refreshData = async (): Promise<void> => {
    this.successMessage = "";
    await this.loadClientPOs();
  };

  private uploadFile = async (file: File): Promise<string> => {
    const cached = this.uploadedUrls.get(file);
    if (cached) return cached;
    const url = await this.imageAPI.uploadFile(
      file,
      CLIENT_PO_UPLOAD_CONTAINER,
    );
    if (!url) {
      throw new Error("Failed to upload PO document. Please try again.");
    }
    this.uploadedUrls.set(file, url);
    return url;
  };

  /**
   * Uploads the PDF, then adds the PO. Sets `addError` and rethrows on failure.
   * The uploaded URL is kept for the same file, so a retry skips the upload.
   */
  addClientPO = async (poNumber: string, file: File): Promise<void> => {
    this.isAdding = true;
    this.addError = "";
    this.successMessage = "";
    try {
      const orderId = this.requireOrderId();
      if (!this.canAddMore) {
        throw new Error(
          `An order can have at most ${MAX_CLIENT_POS_PER_ORDER} client POs.`,
        );
      }
      const trimmed = poNumber.trim();
      const poUrl = await this.uploadFile(file);
      const list = await this.orderAPI.addClientPO(orderId, trimmed, poUrl);
      runInAction(() => {
        this.setList(list);
        this.uploadedUrls.delete(file);
        this.successMessage = `Client PO ${trimmed} added.`;
      });
    } catch (error: any) {
      const message = extractErrorMessage(error, "Failed to add client PO.");
      runInAction(() => {
        this.addError = message;
      });
      throw new Error(message);
    } finally {
      runInAction(() => {
        this.isAdding = false;
      });
    }
  };

  startEdit = (poId: number) => {
    this.editingId = poId;
    this.editError = "";
    this.successMessage = "";
  };

  cancelEdit = () => {
    this.editingId = null;
    this.editError = "";
  };

  /** Saves a changed PO number and/or a replacement PDF. Returns true on success. */
  updateClientPO = async (
    poId: number,
    poNumber: string,
    file: File | null,
  ): Promise<boolean> => {
    const current = this.clientPOs.find((po) => po.id === poId);
    const trimmed = poNumber.trim();
    const body: UpdateClientPORequest = {};
    if (trimmed && trimmed !== (current?.po_number ?? "")) {
      body.po_number = trimmed;
    }

    this.isSavingEdit = true;
    this.editError = "";
    this.successMessage = "";
    try {
      const orderId = this.requireOrderId();
      if (!trimmed) throw new Error("PO number is required.");
      if (file) body.po_url = await this.uploadFile(file);
      if (!body.po_number && !body.po_url) {
        runInAction(() => {
          this.editingId = null;
        });
        return true;
      }
      const list = await this.orderAPI.updateClientPO(orderId, poId, body);
      runInAction(() => {
        this.setList(list);
        if (file) this.uploadedUrls.delete(file);
        this.editingId = null;
        this.successMessage = `Client PO ${trimmed} updated.`;
      });
      return true;
    } catch (error: any) {
      runInAction(() => {
        this.editError = extractErrorMessage(
          error,
          "Failed to update client PO.",
        );
      });
      return false;
    } finally {
      runInAction(() => {
        this.isSavingEdit = false;
      });
    }
  };

  toggleSelected = (poId: number) => {
    this.selectedIds = this.selectedIds.includes(poId)
      ? this.selectedIds.filter((id) => id !== poId)
      : [...this.selectedIds, poId];
  };

  toggleSelectAll = () => {
    this.selectedIds = this.allSelected
      ? []
      : this.clientPOs.map((po) => po.id);
  };

  /** Removes the given POs, or every PO when `poIds` is "all". Returns true on success. */
  removeClientPOs = async (poIds: number[] | "all"): Promise<boolean> => {
    this.isRemoving = true;
    this.removeError = "";
    this.successMessage = "";
    try {
      const orderId = this.requireOrderId();
      const result = await this.orderAPI.removeClientPOs(
        orderId,
        poIds === "all" ? { all: true } : { po_ids: poIds },
      );
      runInAction(() => {
        this.setList(result?.client_pos ?? []);
        const count = result?.removed_ids?.length ?? 0;
        this.successMessage = `Removed ${count} client PO${count === 1 ? "" : "s"}.`;
      });
      return true;
    } catch (error: any) {
      runInAction(() => {
        this.removeError = extractErrorMessage(
          error,
          "Failed to remove client PO(s).",
        );
      });
      return false;
    } finally {
      runInAction(() => {
        this.isRemoving = false;
      });
    }
  };

  /** Fetches the PO's PDF. Sets `downloadError` and returns null on failure. */
  downloadClientPO = async (
    po: OrderClientPO,
  ): Promise<APIBlobResponse | null> => {
    this.downloadingId = po.id;
    this.downloadError = "";
    try {
      if (!po.po_url) throw new Error("This PO has no document.");
      const response = await HelperAPI.downloadAFile(po.po_url);
      if (!response?.data) throw new Error("Failed to download PO document.");
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.downloadError = extractErrorMessage(
          error,
          "Failed to download PO document.",
        );
      });
      return null;
    } finally {
      runInAction(() => {
        this.downloadingId = null;
      });
    }
  };
}
